import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
  useEffect,
  useContext
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  DefaultMenuItem,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  MenuItemDef,
  PaginationChangedEvent,
  ProcessCellForExportParams,
  RowNode,
  SortDirection
} from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setFilters } from '@/app/common/reducers/filterSlice';
import { useUpdateTableSettingsMutation } from '@/app/common/api/tableSettingApi';
import {
  successMessage,
  errorMessage,
  defaultMessage
} from '@/utils/toast';
import { createColumnsDef } from '@/app/common/utils/createColumnsDef';
import { processCellForClipboard, valueFormatter } from './configs';
import useDomain from '@/hooks/useDomain';
import AgGridLoading from './AgGridLoading';
import AgGridButtons from './AgGridButtons';
import AppContext from '@/context/Context';
import AG_GRID_LOCALE_FA from '@/locales/AG_GRID_LOCALE_FA';
import ExportHelpAlert from '../../../../components/common/ExportHelpAlert';
import {
  handleSelectedKeys,
  resetSelectedKeys
} from '@/app/KeywordTracker/reducers/selectedKeys';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { showDrawer } from '@/app/common/reducers/drawer';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import { showModal } from '../../reducers/simpleModal';
import AgGridHeader from './AgGridHeader';
import AgGridGroupHeader from './AgGridGroupHeader';
import keywordsApi, {
  useGetTariffInfoQuery
} from '@/app/KeywordTracker/api/keywordsApi';
import {
  assignForwardedRef,
  type AgGridColumnDef,
  type AgGridExportRequest,
  type AgGridFetchResponse,
  type AgGridHeight,
  type AgGridRowData,
  type AgGridSidebarConfig,
  type AgGridWrapperTrackerProps
} from './types';
import type { FiltersRootState } from '../filters/filterTypes';

interface PreviousPathState {
  path: string;
  query: string;
}

type TrackerContextMenuParams = GetContextMenuItemsParams<AgGridRowData> & {
  node: RowNode<AgGridRowData> & { data: AgGridRowData };
};

const AgGridWrapperTracker = React.forwardRef<
  AgGridReact<AgGridRowData>,
  AgGridWrapperTrackerProps
>(
  (
    {
      table_name,
      columns = [],
      otherOptions = {},
      data,
      height = '500px',
      fetchFunction = null,
      shouldRevalidateFirstPage = null,
      setShouldRevalidateFirstPage = null,
      additionalData = {},
      additionalContext = [],
      otherButtons,
      total,
      perPage = 50,
      page_depth = 1 /* عمق صفحه */,
      settings = {
        pagination: true,
        fullscreen: false,
        changeHeight: false,
        saveGrid: false,
        resetGrid: false,
        sideBar: false,
        masterDetail: null,
        export: {
          enabled: false,
          fetchFunction: null,
          additionalData: null
        }
      }
    },
    ref
  ) => {
    const [perPageState, setPerPageState] = useState(perPage);
    const isFirstLoad = useRef(false);
    const isMdUp = useMediaQuery(breakpointsMediaQuery.md);

    const exportMsg =
      'خروجی شما در حال پردازش است، پس از اتمام در بخش نوتیفیکیشن میتونید فایل رو دانلود کنید..';
    const { domain_slug } = useDomain();
    const { slug } = useParams();
    const job_page = slug === 'last' ? domain_slug : slug;
    const dispatch = useDispatch();

    // این خط چک میکند که اگر ref داشتیم به wrapper اضافه کند و اگر نداشتیم پیش فرض قرار دهد .
    const gridRef = useRef<AgGridReact<AgGridRowData> | null>(null);
    const setGridRef = useCallback(
      (instance: AgGridReact<AgGridRowData> | null) => {
        gridRef.current = instance;
        assignForwardedRef(ref, instance);
      },
      [ref]
    );

    const paramsRef = useRef<ReturnType<typeof useParams> | null>(null);
    const viewRef = useRef<HTMLDivElement | null>(null);
    const heightStateBackUp = useRef<AgGridHeight | undefined>(undefined);
    const params = useParams();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [heightState, setHeightState] = useState<AgGridHeight>(height);
    const [forceResteFilters, setForceResetFilters] = useState(false);
    const [updateTableSettings, { isLoading: isLoadingTableSettings }] =
      useUpdateTableSettingsMutation();
    useGetUserInfoQuery();
    const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
      useGetTariffInfoQuery(domain_slug);
    const isAccessAllowed = () =>
      trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

    const filters = useSelector((state: FiltersRootState) => state.filters);
    const {
      config: { isRTL, theme }
    } = useContext(AppContext);
    let searchQuery = '';

    useEffect(() => {
      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          setHeightState(heightStateBackUp.current || heightState);
        }
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);

      return () => {
        document.removeEventListener(
          'fullscreenchange',
          handleFullscreenChange
        );
        dispatch(resetSelectedKeys());
      };
    }, []);

    /* 
      این بخش برای ری لود شدن مجدد جدول بعد از اعمال فیلتر است
      details=internal => برای جدا کردن کویری استرینگ صفحات و صفحاتی ک در دراور باز میشن 
      کاری کردم که وقتی صفحات دراور باز میشن جدول های صفحه اول یا صفحات قبل مجددا رندر نشن
    */
    if (
      location.search.split('details=internal')[page_depth - 1].includes('FBy')
    ) {
      searchQuery = location.search.split('details=internal')[page_depth - 1]; // گرفتن کویری استرینگ هر صفحه بر اساس عمق
      searchQuery = searchQuery.startsWith('?')
        ? searchQuery.substring(1)
        : searchQuery; // حذف علامت سوال از ابتدای کویری
      searchQuery = searchQuery.endsWith('&')
        ? searchQuery.slice(0, -1)
        : searchQuery; // حذف علامت & از انتهای آدرس
    } else if (location.search.includes('issues=')) {
      // برای فهرست بازبینی خزش
      searchQuery = searchParams.get('issues') ?? '';
    }

    useEffect(() => {
      paramsRef.current = params;
    }, [params]);

    const loadingCellRenderer = useMemo(() => AgGridLoading, []);

    /* AG Grid Columns */
    const columnsDef = useMemo<AgGridColumnDef[]>(
      () =>
        createColumnsDef(
          columns as Parameters<typeof createColumnsDef>[0]
        ) as AgGridColumnDef[],
      []
    );
    /* AG Grid Components */
    const components = useMemo(
      () => ({
        agColumnHeader: AgGridHeader,
        agColumnGroupHeader: AgGridGroupHeader
      }),
      [gridRef?.current?.api.getColumnState()]
    );
    /* AG Grid Loading Message */
    const loadingCellRendererParams = useMemo(
      () => ({ loadingMessage: ' درحال بارگذاری اطلاعات...' }),
      []
    );

    const createServerSideDatasource =
      (): IServerSideDatasource<AgGridRowData> => {
      const serverFetchFunction = fetchFunction;
      return {
        getRows: async (
          params: IServerSideGetRowsParams<AgGridRowData>
        ) => {
          const pageSize =
            gridRef.current?.api.paginationGetPageSize() ?? perPage;
          const previousPath = location.state as PreviousPathState | null; // دریافت مسیر قبلی کاربر
          const storedFilters = settings?.columnsState?.filters;
          const saveFilters =
            !forceResteFilters && storedFilters
              ? Object.keys(storedFilters).length !== 0
                ? storedFilters
                : null
              : null;
          let newFilters: Record<string, unknown> =
            Object.keys(filters).length !== 0
              ? { ...filters }
              : { ...(saveFilters ?? {}) };
          if (previousPath) {
            //اگر وارد مسیر جدیدی شده بود
            if (
              previousPath.path !== location.pathname || //اگر از صفحه یا تبی وارد صفحه دیگر شد
              !searchParams.toString().includes(previousPath.query) // اگر درون یک صفحه مثل چک لیست بود و به آدرس های مختلف میرفت
            ) {
              newFilters = {};
              dispatch(setFilters({}));
            }
          }
          const { sortModel, endRow } = params.request;
          const page = (endRow ?? pageSize) / pageSize;
          const body_data: Record<string, unknown> = {
            perPage: pageSize,
            page,
            filters: newFilters,
            ...additionalData
          };
          if (sortModel.length > 0)
            body_data.sort = {
              column: sortModel[0].colId,
              order: sortModel[0].sort
            };

          if (
            (data &&
              page === 1 &&
              perPage === pageSize &&
              sortModel.length === 0 &&
              Object.keys(filters).length === 0) ||
            isFirstLoad.current
          ) {
            params.success({
              rowData: (data ?? []) as unknown as AgGridRowData[],
              rowCount: total
            });
            isFirstLoad.current = false; // فقط یک بار اجرا بشه
          } else if (serverFetchFunction) {
            await serverFetchFunction(body_data)
              .unwrap()
              .then(response => {
                const { rows, total: total_entities } = (
                  response as AgGridFetchResponse
                ).data;
                params.success({
                  rowData: rows,
                  rowCount: total_entities
                });
              })
              .catch(err => {
                console.error('Error fetching data:', err);
                params.fail();
              });
          } else {
            params.fail();
          }
        }
      };
    };

    const serverSideDatasource = useMemo(() => {
      return createServerSideDatasource();
    }, [paramsRef, searchQuery, data]);

    // /* اعمال تنظیمات جدول */
    const onFirstDataRendered = useCallback(
      (_params: FirstDataRenderedEvent<AgGridRowData>) => {
      if (settings?.columnsState) {
        const tableSettings = settings?.columnsState;
        if (tableSettings.columns) {
          window.colState = tableSettings.columns;
          gridRef?.current?.api.applyColumnState({
            state: window.colState,
            applyOrder: true
          });

          const sortResult = tableSettings.columns.find(
            item => item.sort !== null
          );

          if (sortResult) {
            isFirstLoad.current = true;
          }
        }
      }
      },
      [settings]
    );

    /* فیلتر کردن و شخصی سازی کلیک راست جدول */
    const getContextMenuItems = useCallback(
      (rawParams: GetContextMenuItemsParams<AgGridRowData>) => {
      const params = rawParams as TrackerContextMenuParams;
      if (params.defaultItems) {
        const defaultMenuItems = params.defaultItems.slice();
        const filteredItems = defaultMenuItems.filter(function (item) {
          return (
            item !== 'export' &&
            item !== 'copyWithGroupHeaders' &&
            item !== 'cut' &&
            item !== 'paste' &&
            item !== 'separator'
          );
        });
        const sorts = params.api
          .getColumnState()
          .filter(item => item.sort !== null);

        const finalResult: (
          | DefaultMenuItem
          | MenuItemDef<AgGridRowData>
        )[] = [
          ...filteredItems,
          {
            name: 'تمام صفحه',
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="expand-arrows-alt" class="svg-inline--fa fa-expand-arrows-alt fa-w-14 fs--1" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M448 344v112a23.94 23.94 0 0 1-24 24H312c-21.39 0-32.09-25.9-17-41l36.2-36.2L224 295.6 116.77 402.9 153 439c15.09 15.1 4.39 41-17 41H24a23.94 23.94 0 0 1-24-24V344c0-21.4 25.89-32.1 41-17l36.19 36.2L184.46 256 77.18 148.7 41 185c-15.1 15.1-41 4.4-41-17V56a23.94 23.94 0 0 1 24-24h112c21.39 0 32.09 25.9 17 41l-36.2 36.2L224 216.4l107.23-107.3L295 73c-15.09-15.1-4.39-41 17-41h112a23.94 23.94 0 0 1 24 24v112c0 21.4-25.89 32.1-41 17l-36.19-36.2L263.54 256l107.28 107.3L407 327.1c15.1-15.2 41-4.5 41 16.9z"></path></svg>',
            action: () => {
              if (!document.fullscreenElement) {
                setHeightState(prev => {
                  heightStateBackUp.current = prev;
                  return '100vh';
                });
                viewRef.current?.requestFullscreen();
              } else {
                document.exitFullscreen();
              }
            }
          }
        ];
        if (location.pathname.includes('/keyword-tracker/keywords/'))
          finalResult.push(
            'separator',
            {
              name: 'تحلیل AI (جدید)',
              cssClasses: ['text-success'],
              icon: '<svg fill="rgba(var(--falcon-success-rgb), var(--falcon-text-opacity)) !important" xmlns="http://www.w3.org/2000/svg" id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24" style="width: 13px;"><path d="m19.026,12v6c0,.552-.448,1-1,1s-1-.448-1-1v-6c0-.552.448-1,1-1s1,.448,1,1Zm-7.42-5.283l3.071,11.029c.175.63-.298,1.254-.953,1.254-.443,0-.831-.294-.952-.72l-.643-2.28h-5.206l-.643,2.28c-.12.426-.509.72-.952.72h0c-.654,0-1.128-.624-.953-1.254l3.091-11.108c.141-.608.541-1.12,1.098-1.405.568-.292,1.22-.31,1.839-.05.587.246,1.037.817,1.204,1.535Zm-.041,7.283l-1.929-6.835c-.029-.114-.191-.114-.219,0l-1.929,6.835h4.077Zm11.462-4c-.552,0-1,.448-1,1v8c0,1.654-1.346,3-3,3H5.026c-1.654,0-3-1.346-3-3V5c0-1.654,1.346-3,3-3h8c.552,0,1-.448,1-1S13.578,0,13.026,0H5.026C2.269,0,.026,2.243.026,5v14c0,2.757,2.243,5,5,5h14c2.757,0,5-2.243,5-5v-8c0-.552-.448-1-1-1Zm-6.85-4.82l1.868.787.745,1.865c.161.404.552.668.987.668s.825-.265.987-.668l.741-1.854,1.854-.741c.404-.161.668-.552.668-.987s-.265-.825-.668-.987l-1.854-.741-.741-1.854C20.601.265,20.21,0,19.776,0s-.825.265-.987.668l-.737,1.843-1.84.697c-.406.154-.678.54-.686.974-.008.435.25.83.65.999Z"></path></svg>',
              action: () => {
                dispatch(
                  showDrawer({
                    key: 'ai_notes',
                    payload: {
                      keyword_id: params.node.data.id,
                      keyword: params.node.data.title,
                      nodeData: params.node.data
                    }
                  })
                );
              }
            },
            {
              name: 'نمودار روند رتبه',
              icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chart-line" class="svg-inline--fa fa-chart-line fa-w-16 cursor-pointer little-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M496 384H64V80c0-8.84-7.16-16-16-16H16C7.16 64 0 71.16 0 80v336c0 17.67 14.33 32 32 32h464c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16zM464 96H345.94c-21.38 0-32.09 25.85-16.97 40.97l32.4 32.4L288 242.75l-73.37-73.37c-12.5-12.5-32.76-12.5-45.25 0l-68.69 68.69c-6.25 6.25-6.25 16.38 0 22.63l22.62 22.62c6.25 6.25 16.38 6.25 22.63 0L192 237.25l73.37 73.37c12.5 12.5 32.76 12.5 45.25 0l96-96 32.4 32.4c15.12 15.12 40.97 4.41 40.97-16.97V112c.01-8.84-7.15-16-15.99-16z"></path></svg>',
              action: () => {
                dispatch(
                  showDrawer({
                    key: 'keyword_progress',
                    payload: {
                      keyword_id: params.node.data.id,
                      keyword: params.node.data.title,
                      nodeData: params.node.data
                    }
                  })
                );
              }
            },
            {
              name: 'سابقه نتایج گوگل',
              icon: '<svg aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" class="svg-inline--fa fa-google fa-w-16 cursor-pointer little-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path></svg>',
              action: () => {
                dispatch(
                  showDrawer({
                    key: 'google_result_history',
                    payload: {
                      keyword_id: params.node.data.id,
                      keyword: params.node.data.title
                    }
                  })
                );
              }
            },
            {
              name: 'سابقه رتبه',
              icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="history" class="svg-inline--fa fa-history fa-w-16 cursor-pointer little-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M504 255.531c.253 136.64-111.18 248.372-247.82 248.468-59.015.042-113.223-20.53-155.822-54.911-11.077-8.94-11.905-25.541-1.839-35.607l11.267-11.267c8.609-8.609 22.353-9.551 31.891-1.984C173.062 425.135 212.781 440 256 440c101.705 0 184-82.311 184-184 0-101.705-82.311-184-184-184-48.814 0-93.149 18.969-126.068 49.932l50.754 50.754c10.08 10.08 2.941 27.314-11.313 27.314H24c-8.837 0-16-7.163-16-16V38.627c0-14.254 17.234-21.393 27.314-11.314l49.372 49.372C129.209 34.136 189.552 8 256 8c136.81 0 247.747 110.78 248 247.531zm-180.912 78.784l9.823-12.63c8.138-10.463 6.253-25.542-4.21-33.679L288 256.349V152c0-13.255-10.745-24-24-24h-16c-13.255 0-24 10.745-24 24v135.651l65.409 50.874c10.463 8.137 25.541 6.253 33.679-4.21z"></path></svg>',
              action: () => {
                dispatch(
                  showDrawer({
                    key: 'rank_history',
                    payload: {
                      keyword_id: params.node.data.id,
                      keyword: params.node.data.title
                    }
                  })
                );
              }
            },
            {
              name: 'کلمات مرتبط',
              icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="search" class="svg-inline--fa fa-search fa-w-16 cursor-pointer little-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"></path></svg>',
              action: () => {
                dispatch(
                  showDrawer({
                    key: 'related_keywords',
                    payload: {
                      location_code: params.node.data.location_code,
                      language_code: params.node.data.language_code,
                      keyword: params.node.data.title
                    }
                  })
                );
              }
            },
            'separator',
            {
              name: 'یادداشت گذاری',
              icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="search" class="svg-inline--fa fa-search fa-w-16 cursor-pointer little-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 32C114.6 32 0 125.1 0 240c0 49.6 21.4 95 57 130.7C44.5 421.1 2.7 466 2.2 466.5c-2.2 2.3-2.8 5.7-1.5 8.7S4.8 480 8 480c66.3 0 116-31.8 140.6-51.4 32.7 12.3 69 19.4 107.4 19.4 141.4 0 256-93.1 256-208S397.4 32 256 32z"/></svg>',
              action: () => {
                dispatch(
                  showDrawer({
                    key: 'keyword_notes',
                    payload: {
                      keyword_id: params.node.data.id,
                      keyword: params.node.data.title
                    }
                  })
                );
              }
            }
          );
        const exportSettings = settings.export;
        if (exportSettings?.enabled) {
          const columnsArr = gridRef.current?.api.getAllDisplayedColumns() ?? [];
          const columnsObject: Record<
            string,
            { title: string; width: number; order: number }
          > = {};
          let count = 1;
          columnsArr.forEach(column => {
            const columnDefinition = column.getUserProvidedColDef();
            columnsObject[column.getColId()] = {
              title: columnDefinition?.headerName ?? '',
              width: column.getActualWidth(),
              order: count++
            };
          });

          finalResult.push('separator', {
            name: 'خروجی گرفتن',
            icon: '<span class="ag-icon ag-icon-save" unselectable="on" role="presentation"></span>',
            subMenu: [
              {
                icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
                name: 'خروجی از صفحه',
                disabled: false,
                action: () => {
                  let bodyReq: AgGridExportRequest = {
                    domain_slug,
                    job_page,
                    table_name,
                    filters,
                    offset:
                      params.api.paginationGetCurrentPage() *
                      params.api.paginationGetPageSize(),
                    limit: params.api.paginationGetPageSize(),
                    columns: columnsObject,
                    export_type: 'excel'
                  };

                  if (additionalData && 'from_date' in additionalData) {
                    bodyReq = {
                      ...bodyReq,
                      from_date: additionalData.from_date,
                      to_date: additionalData.to_date
                    };
                  }

                  if (exportSettings.additionalData) {
                    bodyReq = {
                      ...bodyReq,
                      ...exportSettings.additionalData
                    };
                  }

                  if (sorts.length > 0) {
                    bodyReq.sort = {
                      column: sorts[0].colId,
                      order: sorts[0].sort
                    };
                  }
                  exportSettings.fetchFunction?.(bodyReq);

                  defaultMessage(exportMsg);
                }
              },
              {
                name: 'خروجی کل',
                disabled: false,
                icon: '<span class="ag-icon ag-icon-excel" unselectable="on" role="presentation"></span>',
                action: () => {
                  let bodyReq: AgGridExportRequest = {
                    domain_slug,
                    job_page,
                    table_name,
                    filters,
                    offset: 0,
                    limit: 1000000,
                    columns: columnsObject,
                    export_type: 'excel'
                  };

                  if (additionalData && 'from_date' in additionalData) {
                    bodyReq = {
                      ...bodyReq,
                      from_date: additionalData.from_date,
                      to_date: additionalData.to_date
                    };
                  }

                  if (exportSettings.additionalData) {
                    bodyReq = {
                      ...bodyReq,
                      ...exportSettings.additionalData
                    };
                  }

                  if (sorts.length > 0) {
                    bodyReq.sort = {
                      column: sorts[0].colId,
                      order: sorts[0].sort
                    };
                  }
                  exportSettings.fetchFunction?.(bodyReq);
                  defaultMessage(exportMsg);
                }
              }
            ]
          });
        } else {
          // finalResult.push('separator', 'export');
        }
        if (additionalContext.length > 0)
          finalResult.push(...additionalContext);
        if (location.pathname.includes('/landing-urls'))
          finalResult.push('separator', {
            name: 'حذف',
            disabled: !isAccessAllowed(),
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-can" class="svg-inline--fa fa-trash-can fs-0" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"></path></svg>',
            action: () => {
              dispatch(
                handleSelectedKeys([
                  {
                    id: params.node.data.id,
                    label: params.node.data.url
                  }
                ])
              );
              dispatch(showModal('DELETE_LANDING'));
            },
            cssClasses: ['text-danger']
          });
        if (location.pathname.includes('/tags'))
          finalResult.push('separator', {
            name: 'حذف',
            disabled: !isAccessAllowed(),
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-can" class="svg-inline--fa fa-trash-can fs-0" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"></path></svg>',
            action: () => {
              dispatch(
                handleSelectedKeys([
                  { id: params.node.data.id, label: params.node.data.title }
                ])
              );
              dispatch(showModal('DELETE_TAG'));
            },
            cssClasses: ['text-danger']
          });
        if (location.pathname.includes('/keywords'))
          finalResult.push('separator', {
            name: 'حذف',
            disabled: !isAccessAllowed(),
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-can" class="svg-inline--fa fa-trash-can fs-0" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"></path></svg>',
            action: () => {
              dispatch(
                handleSelectedKeys([
                  { id: params.node.data.id, label: params.node.data.title }
                ])
              );
              dispatch(showModal('DELETE_KEYWORD'));
            },
            cssClasses: ['text-danger']
          });
        if (location.pathname.includes('/groups'))
          finalResult.push('separator', {
            name: 'حذف',
            disabled: !isAccessAllowed(),
            icon: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash-can" class="svg-inline--fa fa-trash-can fs-0" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0L284.2 0c12.1 0 23.2 6.8 28.6 17.7L320 32l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32L32 96C14.3 96 0 81.7 0 64S14.3 32 32 32l96 0 7.2-14.3zM32 128l384 0 0 320c0 35.3-28.7 64-64 64L96 512c-35.3 0-64-28.7-64-64l0-320zm96 64c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16l0 224c0 8.8 7.2 16 16 16s16-7.2 16-16l0-224c0-8.8-7.2-16-16-16z"></path></svg>',
            action: () => {
              dispatch(
                handleSelectedKeys([
                  { id: params.node.data.id, label: params.node.data.title }
                ])
              );
              dispatch(showModal('DELETE_GROUP'));
            },
            cssClasses: ['text-danger']
          });
        return finalResult;
      }
      return [];
      },
      [additionalContext, isAccessAllowed, location.pathname, settings]
    );

    /* حذف قابلیت های غیرضروری جدول */
    const defaultColDef = useMemo<ColDef<AgGridRowData>>(() => {
      return {
        // DECODER
        // valueFormatter: p => (p?.value ? decodeURIComponent(p.value) : ''),
        cellDataType: false,
        valueFormatter: valueFormatter,
        filter: false,
        sortingOrder: ['desc', 'asc', null] as SortDirection[],
        suppressHeaderMenuButton: false,
        // floatingFilterComponentParams: { suppressFilterButton: true },
        floatingFilter: false,
        resizable: true,
        sortable: true
      };
    }, []);

    /* ذخیره تنظیمات جدول */
    const saveColumnsState = async () => {
      const api = gridRef.current?.api;
      if (!api) return;
      const columns = api.getColumnState();
      window.colState = columns;
      await updateTableSettings({
        domain_slug: params.domain_slug,
        table_name: location.pathname.split('/').slice(1, 3).join('/'),
        filters,
        columns
      })
        .unwrap()
        .then(_ => {
          successMessage('ذخیره تنظیمات با موفقیت انجام شد.');
        })
        .catch(err => {
          console.log(err);
          errorMessage('در ذخیره تنظیمات خطایی رخ داده است.');
        });
    };

    const onSelectionChanged = useCallback(() => {
      const selectedKeys: Array<{ id: unknown; label: unknown }> = [];
      const currentPage =
        gridRef.current?.api.paginationGetCurrentPage() ?? 0;
      const pageSize = gridRef.current?.api.paginationGetPageSize() ?? perPage;
      const startRow = currentPage * pageSize;
      const endRow = startRow + pageSize;
      gridRef.current?.api.forEachNode(node => {
        const rowIndex = node.rowIndex ?? -1;
        const rowData = node.data;
        if (
          rowData &&
          rowIndex >= startRow &&
          rowIndex < endRow &&
          node.isSelected()
        ) {
          selectedKeys.push({
            id: rowData.id,
            label: rowData.title || rowData.label || rowData.url || ''
          });
        }
      });
      dispatch(handleSelectedKeys(selectedKeys));
    }, [dispatch]);

    // const onSelectionChanged = useCallback(() => {
    //   const selectedRows = gridRef?.current?.api?.getSelectedRows();
    //   let selectedKeys = [];
    //   selectedRows?.map(row => {
    //     selectedKeys.push({
    //       id: row.id,
    //       label: row.title || row.label || row.url || ''
    //     });
    //   });

    //   dispatch(handleSelectedKeys(selectedKeys));
    // }, []);

    const prevPageRef = useRef<number | null>(null);

    const onPaginationChanged = useCallback(
      (params: PaginationChangedEvent<AgGridRowData>) => {
        const currentPage = params.api.paginationGetCurrentPage();
        const prevPage = prevPageRef.current;
        // وقتی اومد صفحه یک از صفحه دیگه و کلمه ای اضافه یا حذف شده بود رفرش شو
        if (
          currentPage === 0 &&
          prevPage !== null &&
          prevPage !== 0 &&
          shouldRevalidateFirstPage
        ) {
          setShouldRevalidateFirstPage?.(false);
          dispatch(keywordsApi.util.invalidateTags(['keywordsPage']));
        }

        // آپدیت prevPage
        prevPageRef.current = currentPage;

        if (params.newPageSize) {
          setPerPageState(params.api.paginationGetPageSize());
        }

        dispatch(handleSelectedKeys([]));
        gridRef?.current?.api?.deselectAll();
      },
      [dispatch]
    );
    /* ریست تنظیمات جدول */
    const resetColumnsState = useCallback(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      api.setFilterModel(null);
      api.resetColumnState(); // ریست تنظیمات ستون ها
      dispatch(setFilters({})); // ریست فیلترهای اعمال شده
      setForceResetFilters(true); // کاری کردیم ک بتونیم فلیترهای دخیره شده رو نادیده بگیریم
      createServerSideDatasource(); // دریافت مجدد داده ها بدون سورت و فیلتر
      setSearchParams(''); // پاک کردن فیلترها از آدرس
    }, []);

    /* AG Grid props */
    let defaultAgProps: AgGridReactProps<AgGridRowData> = {
      suppressRowDrag: !isMdUp,
      getRowId: useCallback(
        (params: GetRowIdParams<AgGridRowData>) => `${params.data.id}`,
        []
      ),
      suppressDragLeaveHidesColumns: !isMdUp,
      suppressMovableColumns: !isMdUp,
      processCellForClipboard: (params: ProcessCellForExportParams<AgGridRowData>) =>
        processCellForClipboard(params, location.pathname),
      defaultExcelExportParams: {
        sheetName: 'export-from-mizfa-tools',
        author: 'mizfa-tools',
        fileName: 'export-from-mizfa-tools',
        processCellCallback(params) {
          return processCellForClipboard(params, location.pathname) ?? '';
        }
      },
      defaultCsvExportParams: {
        fileName: 'export-from-mizfa-tools',
        processCellCallback(params) {
          return processCellForClipboard(params, location.pathname) ?? '';
        }
      },
      loadingCellRenderer: loadingCellRenderer,
      loadingCellRendererParams,
      masterDetail: settings.masterDetail ? true : false,
      detailCellRendererParams: {
        masterDetailData: settings.masterDetail
      },
      domLayout: height === 'auto' ? 'autoHeight' : undefined,
      cellSelection: true,
      getContextMenuItems: getContextMenuItems,
      tooltipShowDelay: 0,
      tooltipHideDelay: 5000,
      pagination: settings.pagination,
      columnDefs: columnsDef,
      // suppressColumnVirtualisation: true,
      // suppressRowVirtualisation: true,
      ...(fetchFunction
        ? {}
        : { rowData: data as unknown as AgGridRowData[] }),
      stopEditingWhenCellsLoseFocus: true,
      enableRtl: isRTL,
      localeText: AG_GRID_LOCALE_FA,
      defaultColDef: defaultColDef,
      rowModelType: fetchFunction ? 'serverSide' : 'clientSide',
      rowSelection: {
        mode: 'multiRow'
      },
      serverSideDatasource: fetchFunction ? serverSideDatasource : undefined,
      onSelectionChanged,
      onPaginationChanged,
      onFirstDataRendered,
      // detailCellRenderer,
      detailRowHeight: 340,
      paginationPageSizeSelector: location.pathname.includes(
        '/keyword-tracker/keywords/'
      )
        ? [50, 100, 200]
        : false,
      paginationPageSize: perPage,
      ...(fetchFunction ? { cacheBlockSize: perPageState } : {}),
      suppressRowTransform: true,
      paginationAutoPageSize: false,
      statusBar: {
        statusPanels: [
          {
            statusPanel: 'agAggregationComponent',
            align: 'left',
            statusPanelParams: {
              aggFuncs: ['avg', 'sum', 'count', 'max', 'min']
            }
          }
        ]
      },
      ...otherOptions
    };

    if (settings?.sideBar) {
      let filteredSideBarProps: AgGridSidebarConfig;
      const toolPanels = [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel'
        }
      ];
      const sidebarProp: AgGridSidebarConfig = {
        sideBar: {
          toolPanels
        }
      };
      if (fetchFunction) {
        filteredSideBarProps = {
          ...sidebarProp,
          sideBar: {
            ...sidebarProp.sideBar,
            toolPanels: toolPanels.filter(
              item => item.id !== 'filters'
            )
          }
        };
      } else {
        filteredSideBarProps = sidebarProp;
      }
      defaultAgProps = { ...defaultAgProps, ...filteredSideBarProps };
    }
    const topScrollRef = useRef<HTMLDivElement | null>(null);
    const [scrollWidth, setScrollWidth] = useState(0);

    const handleGridReady = (_params: GridReadyEvent<AgGridRowData>) => {
      if (!location.pathname.includes('keyword-tracker/keywords')) return;
      const gridEl = viewRef.current;
      const topScrollEl = topScrollRef.current;
      if (!gridEl || !topScrollEl) return;
      const gridViewport = gridEl.querySelector<HTMLElement>(
        '.ag-center-cols-viewport'
      );
      const gridContainer = gridEl.querySelector<HTMLElement>(
        '.ag-center-cols-container'
      );
      if (!gridViewport || !gridContainer) return;
      setScrollWidth(gridContainer.scrollWidth);
      const syncTopToBottom = () => {
        topScrollEl.scrollLeft = gridViewport.scrollLeft;
      };
      const syncBottomToTop = () => {
        gridViewport.scrollLeft = topScrollEl.scrollLeft;
      };
      gridViewport.addEventListener('scroll', syncTopToBottom);
      topScrollEl.addEventListener('scroll', syncBottomToTop);
      return () => {
        gridViewport.removeEventListener('scroll', syncTopToBottom);
        topScrollEl.removeEventListener('scroll', syncBottomToTop);
      };
    };
    return (
      <>
        <ExportHelpAlert />
        <AgGridButtons
          settings={settings}
          heightState={heightState}
          setHeightState={setHeightState}
          saveColumnsState={saveColumnsState}
          isSavingState={isLoadingTableSettings}
          resetColumnsState={resetColumnsState}
          heightStateBackUp={heightStateBackUp}
          ref={viewRef}
          otherButtons={otherButtons}
        />
        {location.pathname.includes('keyword-tracker/keywords') && (
          <div
            ref={topScrollRef}
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              height: 16,
              width: '100%'
            }}
          >
            <div style={{ width: scrollWidth, height: 1 }} />
          </div>
        )}
        <div
          ref={viewRef}
          className={`overflow-hidden rounded-3 ${
            theme === 'dark' ? 'ag-theme-balham-dark' : 'ag-theme-balham'
          } ag-theme-custom`}
          style={{
            height:
              defaultAgProps.domLayout === 'autoHeight'
                ? undefined
                : heightState,
            width: '100%'
          }}
        >
          <AgGridReact<AgGridRowData>
            ref={setGridRef}
            {...defaultAgProps}
            onGridReady={handleGridReady}
            components={components}
            // key={JSON.stringify(gridRef?.current?.api.getColumnState())}
            suppressDragLeaveHidesColumns={true}
            suppressMoveWhenColumnDragging={true}
          />
        </div>
      </>
    );
  }
);

export default memo(AgGridWrapperTracker);
